import { Button } from '@progress/kendo-react-buttons';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import { AccessSharedWith } from 'app/components/access-sharing';
import {
  MachineOwnerCard,
  SharedUserAccess,
} from 'app/components/access-sharing/cards';
import { ChangeUserPermissionsDialog } from 'app/components/access-sharing/dialogs';
import { CounterBadge } from 'app/components/badges';
import { DeleteDialog } from 'app/components/dialogs';
import { AddIcon } from 'app/components/icons';
import { BackActionSplitCardContentLayout } from 'app/components/layouts';
import { ActionsListItem, EllipsisMenu } from 'app/components/menu';

import useHasAccessSharingPermissions from 'app/hooks/useHasAccessSharingPermissions';
import { getDefaultThumbnail } from 'app/pages/Equipment/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import './EquipmentDetailsMachineDetailsPage.less';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import { formatDate } from 'utils/date';
import { api } from 'services/api';
import { useNotificationsContext } from 'context/useNotificationsContext';
import { ErrorNotification, Success } from 'app/components/Notifications';
import { SharedItemShareType } from 'services/api/shares';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import { AccessSharingRole } from 'types/entities/AccessSharingRole';
import useUsername from 'app/hooks/user-info/useUsername';

/**
 * A temporary solution (a copy of the MachineDetailsPage) until business finalizes the requirements for this page.
 * After the MachineDetailsPage can be transformed either into a separate component or a page, and this copy will be removed.
 */
export default function EquipmentDetailsMachineDetailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);
  const { setEquipmentListData, equipmentListData } =
    React.useContext(EquipmentListContext);

  const username = useUsername();

  const { id } = useParams();
  const [revokeInvitation] = api.useRevokeInvitationMutation();
  const { addNotification, addSnackbar } = useNotificationsContext();

  // More appropriate entity name will be set when API is ready.
  const [selectedUserAccessToChange, setSelectedUserAccessToChange] = useState<
    SharedUserAccess | undefined
  >();
  const isChangeUserPermissionsDialogOpen = !!selectedUserAccessToChange;

  const [isRevokeAllUserAccessDialogOpen, setIsRevokeAllUserAccessDialogOpen] =
    useState(false);

  const [selectedUserAccessToRevoke, setSelectedUserAccessToRevoke] = useState<
    SharedUserAccess | undefined
  >();
  const isRevokeUserAccessDialogOpen = !!selectedUserAccessToRevoke;

  const { selectedEquipment: equipment } = useGetEquipmentData(id!);

  const hasAccess = useHasAccessSharingPermissions(equipment?.role);

  const mainMenuActions = useMemo<ActionsListItem[]>(() => {
    return [
      {
        label: t('access_sharing.role_based_actions.remove_all_users'),
        action: () => setIsRevokeAllUserAccessDialogOpen(true),
      },
    ];
  }, [hasAccess, t, setIsRevokeAllUserAccessDialogOpen]);

  const userAccess = useMemo(() => {
    const activeUserAccess = equipment?.shareData?.sharedWith
      .filter(
        sharedWith =>
          !sharedWith.endsAt || new Date(sharedWith.endsAt) > new Date(),
      )
      .map(sharedWith => ({
        id: sharedWith.exposedAccessSharingRecordId,
        firstName: sharedWith.displayName,
        lastName: '',
        email: sharedWith.email,
        role: sharedWith.sharedRole,
        startTime: sharedWith.startsAt
          ? formatDate(sharedWith.startsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
        endTime: sharedWith.endsAt
          ? formatDate(sharedWith.endsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
      }));

    const expiredUserAccess = equipment?.shareData?.sharedWith
      .filter(
        sharedWith =>
          sharedWith.endsAt && new Date(sharedWith.endsAt) < new Date(),
      )
      .map(sharedWith => ({
        id: sharedWith.exposedAccessSharingRecordId,
        firstName: sharedWith.displayName,
        lastName: '',
        email: sharedWith.email,
        role: sharedWith.sharedRole,
        startTime: sharedWith.startsAt
          ? formatDate(sharedWith.startsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
        endTime: sharedWith.endsAt
          ? formatDate(sharedWith.endsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
      }));

    return {
      activeUserAccess,
      expiredUserAccess,
    };
  }, [equipment?.shareData?.sharedWith]);

  React.useEffect(() => {
    const classNameIndex = setClassNames('layout-grey');
    return () => {
      removeClassNames(classNameIndex);
    };
  }, []);

  const handleRevokeAllAccess = async () => {
    const exposedAccessSharingRecordIds = equipment?.shareData?.sharedWith.map(
      sharedWith => sharedWith.exposedAccessSharingRecordId,
    );

    if (!exposedAccessSharingRecordIds) return;

    try {
      await revokeInvitation(
        exposedAccessSharingRecordIds.map(id => ({
          shareType: SharedItemShareType.EQUIPMENT,
          exposedAccessSharingRecordId: id,
        })),
      );
      addSnackbar(
        <div className="snackbar-container">
          <span>
            {t('access_sharing.snackbars.you_removed_all_users_access')}
          </span>
        </div>,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setIsRevokeAllUserAccessDialogOpen(false);
    }
  };

  const handleRevokeAccess = async () => {
    try {
      if (!equipment?.isSharedIndividually) return;
      const exposedAccessSharingRecordId =
        equipment?.shareData?.sharedWith.find(
          sharedWith => sharedWith.email === selectedUserAccessToRevoke?.email,
        )?.exposedAccessSharingRecordId;

      await revokeInvitation([
        {
          shareType: 'equipment',
          exposedAccessSharingRecordId: exposedAccessSharingRecordId!,
        },
      ]);
      addNotification(
        <Success text={t('access_sharing.snackbars.access-revoked')} />,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setSelectedUserAccessToRevoke(undefined);
    }
  };

  useEffect(() => {
    if (
      !equipment?.isSharedIndividually &&
      equipment?.role !== AccessSharingRole.Owner
    ) {
      const equipmentList = equipmentListData.equipmentList?.filter(e => {
        return e.id !== equipmentListData.selectedEquipment?.id;
      });
      const isNotEmpty = equipmentList?.length && equipmentList.length > 0;

      setEquipmentListData(oldData => ({
        ...oldData,
        selectedEquipment: isNotEmpty ? equipmentList[0] : undefined,
        selectedIndex: isNotEmpty ? 0 : undefined,
        equipmentList,
      }));
      if (isNotEmpty) navigate(`/equipment/${equipmentList[0].id}`);
    }
  }, [equipment?.isSharedIndividually]);

  function handleBack() {
    navigate(-1);
  }

  // TODO Request a design
  if (!equipment) {
    return <h1>Equipment Not Found</h1>;
  }

  return (
    <>
      <BackActionSplitCardContentLayout
        className="equipment-details-machine-details-page"
        backActionProps={{ onBack: handleBack }}
        renderHeader={() => (
          <div className="machine-details-page__header">
            <div className="machine-details-page__info-and-picture-wrapper">
              <div className="machine-details-page__info-wrapper">
                <h1 className="machine-details-page__nickname ellipsis">
                  {equipment?.nickName || equipment?.model}
                </h1>

                <div className="machine-details-page__model">
                  <span className="machine-details-page__model-label">
                    {t('equipment_model')}
                  </span>
                  {equipment.model}
                  <span className="machine-details-page__model-label">
                    {t('equipment_serial')}
                  </span>
                  {equipment.serial}
                </div>
              </div>

              <div className="machine-details-page__picture">
                <img
                  src={
                    equipment?.modelIconUrl ||
                    getDefaultThumbnail(equipment?.type, equipment?.subcategory)
                  }
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = getDefaultThumbnail(
                      equipment?.type,
                      equipment?.subcategory,
                    );
                  }}
                  alt="Machine"
                />
              </div>
            </div>

            <MachineOwnerCard
              firstName={
                equipment.shareData?.sharedItemDetails.ownerName ?? username
              }
              lastName={''}
              email={equipment.shareData?.sharedItemDetails.ownerEmail}
              className="machine-details-page__owner-card"
            />
          </div>
        )}
        renderContent={() => (
          <div className="machine-details-page__content">
            <div className="machine-details-page__user-actions-wrapper">
              <CounterBadge
                badgeContent={
                  (userAccess.activeUserAccess?.length ?? 0) +
                  (userAccess.expiredUserAccess?.length ?? 0)
                }
                text={t('shared_with')}
              />

              <div className="machine-details-page__user-actions">
                {hasAccess('addNewUser') && (
                  <Button
                    className="machine-details-page__add-new-user-btn"
                    themeColor="secondary"
                    onClick={() =>
                      navigate(
                        `/equipment/${equipment.id}/manage-users/add-new-user`,
                      )
                    }
                  >
                    <AddIcon strokeWidth={2} />
                    {t('add_user')}
                  </Button>
                )}

                {hasAccess('revokeAllUsersAccess') && (
                  <EllipsisMenu actions={mainMenuActions} />
                )}
              </div>
            </div>

            <AccessSharedWith
              role={equipment.role!}
              userAccess={userAccess.activeUserAccess || []}
              expiredUserAccess={userAccess.expiredUserAccess || []}
              onChangeAccess={setSelectedUserAccessToChange}
              onRevokeAccess={setSelectedUserAccessToRevoke}
            />
          </div>
        )}
      />

      {isChangeUserPermissionsDialogOpen && (
        <ChangeUserPermissionsDialog
          equipment={equipment}
          userAccess={selectedUserAccessToChange}
          onClose={() => setSelectedUserAccessToChange(undefined)}
        />
      )}

      <DeleteDialog
        open={isRevokeUserAccessDialogOpen}
        title={t('revoke_access_confirmation', {
          email: selectedUserAccessToRevoke?.email,
          machineName: equipment.nickName || equipment.model,
        })}
        confirmationLabel={t('access_sharing.role_based_actions.revoke')}
        onClose={() => {
          setSelectedUserAccessToRevoke(undefined);
        }}
        onConfirm={handleRevokeAccess}
      />

      <DeleteDialog
        open={isRevokeAllUserAccessDialogOpen}
        title={t('revoke_access_confirmation_all', {
          machineName: equipment.nickName || equipment.model,
        })}
        confirmationLabel={t('access_sharing.role_based_actions.revoke')}
        onClose={() => setIsRevokeAllUserAccessDialogOpen(false)}
        onConfirm={handleRevokeAllAccess}
      />
    </>
  );
}
